import { Injectable, inject, ViewChild } from '@angular/core';
import { child, Database, equalTo, get, listVal, objectVal, onValue, orderByChild, push, query, ref, remove, set, update } from '@angular/fire/database';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subject, catchError, firstValueFrom, forkJoin, map, of, take, tap, concat } from 'rxjs';
import { iAcuerdo, iCatalogos, iComentarios, iEntrada, iEntregables } from './entrada.interface';
import { iUsuario } from '../usuario/usuario.interface';
import { Storage, ref as refStorage, getDownloadURL, listAll, uploadBytes, deleteObject, uploadBytesResumable  } from '@angular/fire/storage';
import { getStorage, ref as refFS } from "firebase/storage";
import { constants } from 'buffer';
import moment from 'moment';
import { createTimeout, resolveAfterSeconds } from '../utils';

@Injectable({
  providedIn: 'root'
})
export class EntradaService {
  private database: Database = inject(Database);
  private http = inject(HttpClient);
  private storage = inject(Storage);
  private refEntrada: any;
  public taskFilesEntrada: any[] = [];
  public taskFilesEntregables: any[] = [];

  obtenerEntrada (idEntrada: string, idTema: string) {
    this.refEntrada = ref(this.database, `entrada/${idTema}/${idEntrada}`);
    return objectVal<iEntrada>(this.refEntrada);
  }

  obtenerHistorico (idEntrada: string, idTema: string) {
    this.refEntrada = ref(this.database, `historico/${idTema}/${idEntrada}`);
    return objectVal<any>(this.refEntrada);
  }

  _guardarEntrada (oEntrada: any) {
    this.refEntrada.update(oEntrada);
  }

  getCatalogo_ (tipo: string) {
    const refCatalogo = ref(this.database, `catalogos/${tipo}`);
    const refQuery = query(refCatalogo, orderByChild('activo'), equalTo(true));
    return listVal(refQuery, {keyField: 'id'});
  }

  getCatalogo (tipo: string) {
    const refCatalogo = ref(this.database, `catalogos/${tipo}`);
    return listVal(refCatalogo, {keyField: 'id'});
  }

  getTemas () {
    const refCatalogo = ref(this.database, `temas`);
    const refQuery = query(refCatalogo, orderByChild('activo'), equalTo(true));
    return listVal(refQuery, {keyField: 'id'});
  }

  generarFolio (email:string, nombre:string, fecha:string) {
    return this.http.post(environment.folio.genera, { "email": email, "nombre": nombre, "fecha": fecha }).pipe(
      map((data:any) => {
        return data;
      })
    );
  }

  async getArchivos (idEntrada: string, archivos: any[]): Promise<any[]> {
    var archivosURL = await Promise.all(archivos.map(async (item:any) => {
      const filePath = `entradas/${idEntrada}/${item.name}`;
      const storageRef = refStorage(this.storage, filePath);
      await getDownloadURL(storageRef).then(function (url) {
        item.url = url;
      })
      .catch(function (error) {
        item.url = false;
        item.code = error.code;
        item.error = error.message;
      });
      return item;
    }));

    return archivosURL;
  }

  async getArchivosList (idEntrada: string) {
    const listFiles: any[] = [];
    const listRef = refStorage(this.storage, `entradas/${idEntrada}`);
    const result = await listAll(listRef);

    for(let i in result.items) {
      const itemRef = result.items[i];
      const url = await getDownloadURL(itemRef);
      listFiles.push({
        name: itemRef.name,
        url: url
      });
    }

    return listFiles;
  }

  delArchivo (idEntrada:string, archivo: any) {
    const refStorageArchivo = refStorage(this.storage, `/entradas/${idEntrada}/${archivo.name}`);
    return deleteObject(refStorageArchivo);
  };

  getFormasEnvio (idEntrada: string) {
    const refFormaEnvio = ref(this.database, `formasEnvio/${idEntrada}`);
    return listVal(refFormaEnvio, {keyField: 'id'});
  }

  delFormasEnvio (idEntrada: string, idFormaEnvio: string) {
    const refFormaEnvio = ref(this.database, `formasEnvio/${idEntrada}/${idFormaEnvio}`);
    return remove(refFormaEnvio);
  }

  getComentario (idEntrada: string) {
    const refComentarios = ref(this.database, `comentarios/${idEntrada}`);
    return listVal(refComentarios, {keyField: 'id'});
  }

  delComentario (idEntrada: string, idComentario: string) {
    const refComentarios = ref(this.database, `comentarios/${idEntrada}/${idComentario}`);
    return remove(refComentarios);
  }

  obtenerRelacionados (idEntrada:string, idPadre:string = '') {
    return this.http.post(environment.entradas.relacionados, {idEntrada : idEntrada, idPadre : idPadre}).pipe(
      map((data:any) => {
        return data;
      })
    );
  }

  consultarAcuerdoPorEntrada (idEntrada: string) {
    return this.http.get(`${environment.entradas.consultarAcuerdoPorEntrada}/${idEntrada}`);
  }

  obtenerEntregables (idEntrada: string) {
    const refEntregables = ref(this.database, `entregables/${idEntrada}`);
    return listVal(refEntregables, {keyField: 'id'});
  }

  async obtenerArchivoEntregable (idKey: string, archivos: any[]) {
    var archivosURL = await Promise.all(archivos.map(async (item:any) => {
      const filePath = `entregables/${idKey}/${item.name}`;
      const storageRef = refStorage(this.storage, filePath);
      await getDownloadURL(storageRef).then(function (url) {
        item.url = url;
      })
      .catch(function (error) {
        item.url = false;
        item.code = error.code;
        item.error = error.message;
      });
      return item;
    }));

    return archivosURL;
  }

  getDocumento (idGas: string) {
    const refDocumentos = ref(this.database, `documentos/${idGas}`);
    return objectVal(refDocumentos);
  }

  guardarDocumentoUsed (idGas: string) {
    const refDocumentos = ref(this.database, `documentos/${idGas}`);
    return update(refDocumentos, {used: true});
  }

  guardaEntregable (idKey: string, entrada: iEntrada, entregable: iEntregables, usuario: iUsuario) {
    const refEntregables = ref(this.database, `entregables/${idKey}`);
    const referenciaLogger = ref(this.database, `entregables_logger/${idKey}`);

    return new Promise((resolve, reject) => {
      try {
        let aProsesosArchivos = [];
        let oEntregable = {...entregable};
        oEntregable.archivos = oEntregable.archivos.map((item:any) => { return {"name":item.name, "metadata":item.metadata} });

        let idEntregable = push(refEntregables, {
            tipo: oEntregable.tipo,
            tipo_descripcion: oEntregable.tipo_descripcion,
            estatus: oEntregable.estatus,
            estatus_descripcion: oEntregable.estatus_descripcion,
            comentarios: oEntregable.comentarios,
            archivos: oEntregable.archivos,
            unameUpdate: usuario.nombre,
            umailUpdate: usuario.mail,
            prioridad: 2,

            //DOCUMENTO
            url: oEntregable.url,
            title: oEntregable.title,
            idGas: oEntregable.idGas,
            used: (oEntregable.url)? true : false,

            //DATOS NECESARIOS PARA REPORTES
            fechaInicio: moment(entrada.fechaInicio).format("YYYY-MM-DD"),
            fechaFin: moment(entrada.fechaFin).format("YYYY-MM-DD"),
            entrada: idKey,
            tema: entrada.tema,
            asunto: entrada.asunto,
            analista: entrada.analista,
            analista_descripcion: entrada.analista_descripcion,
            supervisor: entrada.supervisor,
            supervisor_descripcion: entrada.supervisor_descripcion,
            coordinador: entrada.coordinador,
            coordinador_descripcion: entrada.coordinador_descripcion,
            creacion: moment().format("YYYY-MM-DD HH:mm:ss"),
            terminado: moment().format("YYYY-MM-DD HH:mm:ss"),
            timestamp: new Date().getTime()
        }).key;

        //LOGGER PARA ENTREGABLES
        let idLogger = push(referenciaLogger, {
            id: idEntregable,
            tipo_descripcion: oEntregable.tipo_descripcion,
            estatus: oEntregable.estatus,
            estatus_descripcion: oEntregable.estatus_descripcion,
            analista_descripcion: entrada.analista_descripcion,
            supervisor_descripcion: entrada.supervisor_descripcion,
            coordinador_descripcion: entrada.coordinador_descripcion,
            motivo_cancelacion: (oEntregable.motivo_cancelacion)? oEntregable.motivo_cancelacion : "",
            timestamp: new Date().getTime(),
            //USUARIO QUE REALIZA ACTUALIZACION
            unameUpdate: usuario.nombre,
            umailUpdate: usuario.mail
        }).key;

        aProsesosArchivos.push(this.guardarArchivosEntregable(idEntregable, oEntregable.files));

        Promise.all(aProsesosArchivos).then(values => {
            console.log("TERMINA DE CARGA DE ENTREGABLES", values);
            resolve({"idEntrada": idKey, "idEntregable": idEntregable, "idLogger": idLogger});
        }).catch(error => {
            console.log(error);
            reject(error)
        });

      } catch(error) {
        reject(error);
      }
    });
  }

  guardaEntregables (idKey: string, entrada: iEntrada, entregables: iEntregables[], usuario: iUsuario) {
    this.taskFilesEntregables = [];
    const refEntregables = ref(this.database, `entregables/${idKey}`);
    const referenciaLogger = ref(this.database, `entregables_logger/${idKey}`);

    return new Promise(async (resolve, reject) => {
      let aProsesosArchivos: any[] = [];
      try {
        for(let i in entregables) {
          let oEntregable = entregables[i];
          oEntregable.archivos = oEntregable.archivos.map((item:any) => { return {"name":item.name, "metadata":item.metadata} });

          if(oEntregable.id) {
            update(child(refEntregables, oEntregable.id), {
              //ARCHIVOS
              archivos: oEntregable.archivos,

              //DOCUMENTO
              url: oEntregable.url,
              title: oEntregable.title,
              idGas: oEntregable.idGas,
              used: (oEntregable.url != null)?true:false,

              //DATOS NECESARIOS PARA REPORTES
              fechaInicio: moment(entrada.fechaInicio).format("YYYY-MM-DD"),
              fechaFin: moment(entrada.fechaFin).format("YYYY-MM-DD"),
              entrada: idKey,
              tema: entrada.tema,
              asunto: entrada.asunto,
              analista: entrada.analista,
              analista_descripcion: entrada.analista_descripcion,
              supervisor: entrada.supervisor,
              supervisor_descripcion: entrada.supervisor_descripcion,
              coordinador: entrada.coordinador,
              coordinador_descripcion: entrada.coordinador_descripcion,
              timestamp: new Date().getTime()
            });
          } else {
            let idEntregable = push(refEntregables, {
              tipo: oEntregable.tipo,
              tipo_descripcion: oEntregable.tipo_descripcion,
              estatus: oEntregable.estatus,
              estatus_descripcion: oEntregable.estatus_descripcion,
              comentarios: oEntregable.comentarios,
              archivos: oEntregable.archivos,
              unameUpdate: usuario.nombre,
              umailUpdate: usuario.mail,
              prioridad: 2,

              //DOCUMENTO
              url: oEntregable.url,
              title: oEntregable.title,
              idGas: oEntregable.idGas,
              used: (oEntregable.url)? true : false,

              //DATOS NECESARIOS PARA REPORTES
              fechaInicio: moment(entrada.fechaInicio).format("YYYY-MM-DD"),
              fechaFin: moment(entrada.fechaFin).format("YYYY-MM-DD"),
              entrada: idKey,
              tema: entrada.tema,
              asunto: entrada.asunto,
              analista: entrada.analista,
              analista_descripcion: entrada.analista_descripcion,
              supervisor: entrada.supervisor,
              supervisor_descripcion: entrada.supervisor_descripcion,
              coordinador: entrada.coordinador,
              coordinador_descripcion: entrada.coordinador_descripcion,
              creacion: moment().format("YYYY-MM-DD HH:mm:ss"),
              terminado: moment().format("YYYY-MM-DD HH:mm:ss"),
              timestamp: new Date().getTime()
            }).key;

            //LOGGER PARA ENTREGABLES
            let idLogger = push(referenciaLogger, {
                id: idEntregable,
                tipo_descripcion: oEntregable.tipo_descripcion,
                estatus: oEntregable.estatus,
                estatus_descripcion: oEntregable.estatus_descripcion,
                analista_descripcion: entrada.analista_descripcion,
                supervisor_descripcion: entrada.supervisor_descripcion,
                coordinador_descripcion: entrada.coordinador_descripcion,
                motivo_cancelacion: (oEntregable.motivo_cancelacion != undefined)? oEntregable.motivo_cancelacion : "",
                timestamp: new Date().getTime(),
                //USUARIO QUE REALIZA ACTUALIZACION
                unameUpdate: usuario.nombre,
                umailUpdate: usuario.mail
            });

            aProsesosArchivos.push(this.guardarArchivosEntregable(idEntregable, oEntregable.files));
          } 
        }

        return Promise.all(aProsesosArchivos).then(values => {
            console.log("TERMINA DE CARGA DE ENTREGABLES");
            resolve({"idEntrada": idKey});
        }).catch(error => {
            reject(error);
        });

      } catch(error) {
        reject(error);
      }
    });
  }

  actualizaEntregables (idKey: string, entrada: iEntrada, entregables: iEntregables[], usuario: iUsuario, cambioEstatus: boolean) {
    const refEntregables = ref(this.database, `entregables/${idKey}`);
    const referenciaLogger = ref(this.database, `entregables_logger/${idKey}`);

    return new Promise(async (resolve, reject) => {
      try {
        let aProsesosArchivos: any[] = [];
        for(let i in entregables) {
          let oEntregable = entregables[i];
          oEntregable.archivos = oEntregable.archivos.map((item:any) => { return {"name":item.name, "metadata":item.metadata} });

          if(oEntregable.id) {
            let updateEntregablePromesa = update(child(refEntregables, oEntregable.id), {
              estatus: oEntregable.estatus,
              estatus_descripcion: oEntregable.estatus_descripcion,
              comentarios: oEntregable.comentarios,
              archivos: oEntregable.archivos,
              unameUpdate: usuario.nombre,
              umailUpdate: usuario.mail,
              prioridad: (oEntregable.prioridad == undefined)?2:oEntregable.prioridad,

              //CANCELADO
              motivo_cancelacion: (oEntregable.motivo_cancelacion != undefined)?oEntregable.motivo_cancelacion:"",
              terminado: moment().format("YYYY-MM-DD HH:mm:ss"),
              timestamp: new Date().getTime()
            }).then(async (result: any) => {
              console.log("Actualizacion Terminada");
            });
  
            aProsesosArchivos.push(createTimeout(1000));
            aProsesosArchivos.push(updateEntregablePromesa);

            aProsesosArchivos = aProsesosArchivos.concat(this.guardarArchivosEntregable(oEntregable.id, oEntregable.files));

            //LOGGER PARA ENTREGABLES
            if(cambioEstatus) {
              let idLogger = push(referenciaLogger, {
                id: oEntregable.id,
                tipo_descripcion: oEntregable.tipo_descripcion,
                estatus: oEntregable.estatus,
                estatus_descripcion: oEntregable.estatus_descripcion,
                analista_descripcion: entrada.analista_descripcion,
                supervisor_descripcion: entrada.supervisor_descripcion,
                coordinador_descripcion: entrada.coordinador_descripcion,
                motivo_cancelacion: (oEntregable.motivo_cancelacion != undefined)? oEntregable.motivo_cancelacion : "",
                usuario_cancelacion: (oEntregable.estatus == 6)? oEntregable.usuario_cancelacion : null,
                timestamp: new Date().getTime(),
                //USUARIO QUE REALIZA ACTUALIZACION
                unameUpdate: usuario.nombre,
                umailUpdate: usuario.mail
              });
            }
          }
        }

        return Promise.all(aProsesosArchivos).then(values => {
            console.log("TERMINA DE CARGA DE ENTREGABLES");
            resolve({'idEntrada': idKey});
        }).catch(error => {
            reject(error)
        });

      } catch(error) {
        reject({'idTransaccion': idKey, error: error});
      }
    });
  }

  guardarArchivosEntregable (idKeyEntregable: any, archivos: any) {
    let taskFiles: any[] = [];
    const refStorageEntrada = refStorage(this.storage, `/entregables/${idKeyEntregable}`);

    try {
      for(let i in archivos) {
        const archivo = archivos[i];
        if(archivo.file) {
          const refStorageEntradaFile = refStorage(refStorageEntrada, archivo.name);
          let uploadTask = uploadBytesResumable(refStorageEntradaFile, archivo.file);

          try {
            // `await` pausa la ejecución hasta que la subida termina
            //await uploadTask;
            console.log('El archivo se ha subido por completo!');
          } catch (error) {
            console.error('Error durante la subida:', error);
          }

          this.taskFilesEntregables.push(uploadTask);
          this.taskFilesEntrada.push(uploadTask);
          taskFiles.push(uploadTask);
        } 
      }
      return taskFiles;
    } catch (error) {
      return(error);
    }

    /*return new Promise(async (resolve, reject) => {
      try {
        for(let i in archivos) {
          const archivo = archivos[i];
          if(archivo.file) {
            const refStorageEntradaFile = refStorage(refStorageEntrada, archivo.name);
            let uploadTask = uploadBytesResumable(refStorageEntradaFile, archivo.file);

            try {
              // `await` pausa la ejecución hasta que la subida termina
              //await uploadTask;
              console.log('El archivo se ha subido por completo!');
            } catch (error) {
              console.error('Error durante la subida:', error);
            }

            this.taskFilesEntregables.push(uploadTask);
            this.taskFilesEntrada.push(uploadTask);
            taskFiles.push(uploadTask);
          } 
        }
        resolve(taskFiles);
      } catch (error) {
        reject(error);
      }
    });*/
  }

  getTaskUploadingEntrada () {
    return this.taskFilesEntrada;
  }

  getTaskUploadingEntregables () {
    return this.taskFilesEntregables;
  }

  setTaskUploadingEntrada () {
    this.taskFilesEntrada = [];
    this.taskFilesEntregables = [];
  }

  borrarEntregable (idKeyEntrada: any, idKeyEntregable: any) {
    const refEntregables = ref(this.database, `/entregables/${idKeyEntrada}/${idKeyEntregable}`);
    return remove(refEntregables);
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // METODO GUARDADO PARA ENTRADA
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  guardar (edicion: any, entrada: any, comentarios: any, formas_envio: any, archivos: any, acuerdo: any, entregables: any, padre: any, usuario: any, catalogos: any) {
    const refTema = ref(this.database, `entrada/${entrada.tema}`);
    const idKey = (edicion == false)?push(refTema).key: edicion.idEntrada;
    
    //FIX PARA VALIDAR ENTREGABLES
    if(entregables.length > 0 && entrada.analista == '' && entrada.supervisor == '' && entrada.coordinador == '') {
      alert("Se han perdido datos de los responsables en el guardado la entrada COD-002.");
    }

    const allPromise = [
      this.guardarEntrada(idKey, structuredClone(entrada)),
      this.guardarComentario(idKey, comentarios),
      this.guardarFormasEnvio(idKey, formas_envio, structuredClone(entrada)),
      this.guardarArchivos(idKey, archivos),
      //this.guardarAcuerdo(idKey, acuerdo),
      this.guardaSeguimiento(idKey, structuredClone(entrada)),
      this.guardarEntregables(idKey, structuredClone(entrada), entregables, usuario),
    ];

    if(padre != false && padre != null) {
      allPromise.push(this.guardarRelacion(idKey, entrada, padre, catalogos));
    }

    return Promise.all(allPromise);
  }

  guardarEntrada (idkey:any, entrada: any) {
    const promiseEntrada = new Promise((resolve, reject) => {
      const refEntrada = ref(this.database, `entrada/${entrada.tema}/${idkey}`);
      update(refEntrada, entrada).then(() => {
        return resolve(idkey);
      })
      .catch((error) => {
        return reject(error);
      });
    });

    return promiseEntrada;
  }

  guardarComentario (idkey: any, comentarios: any) {
    const refComentario = ref(this.database, `/comentarios/${idkey}`);
    return new Promise((resolve, reject) => {
        try {
          for(const i in comentarios) {
            let oComentario: iComentarios = {
              fecha: comentarios[i].fecha,
              descripcion: comentarios[i].descripcion,
              bb: (comentarios[i].bb != undefined)? comentarios[i].bb : false,
              bb_enviado: (comentarios[i].bb_enviado != undefined)? comentarios[i].bb_enviado : "",
              bb_enviado_descripcion: (comentarios[i].bb_enviado_descripcion != undefined)? comentarios[i].bb_enviado_descripcion : "",
              usuario: (comentarios[i].usuario != undefined)? comentarios[i].usuario : "",
              tsCreation: (comentarios[i].id && comentarios[i].tsCreation)?comentarios[i].tsCreation: new Date().toISOString(),
              tsLastUpdate: new Date().toISOString(),
              tema: comentarios[i].tema
            };

            if(comentarios[i].id) {
              update(child(refComentario, comentarios[i].id), oComentario);
            } else {
              push(refComentario, oComentario);
            }
          }

          resolve(idkey);
        } catch(error) {
          reject(error);
        }
    });
  }

  guardarFormasEnvio (idkey:any, formas_envio:any, entrada:any) {
    const refFormasEnvio = ref(this.database, `/formasEnvio/${idkey}`);
    return new Promise((resolve, reject) => {

      try {
        for(const i in formas_envio) {
          let item: any = formas_envio[i];

          let oForma: any = {
            tipo: item.tipo,
            tipo_descripcion: item.tipo_descripcion,
            enviado: item.enviado,
            enviado_descripcion: item.enviado_descripcion,
            fecha: item.fecha,
            leido: (item.leido)? true : false,
            leido_fecha: item.leido_fecha,
            leido_hora: item.leido_hora,
            reunion_descripcion: item.reunion_descripcion !== undefined ? item.reunion_descripcion : "",

            //DATOS NECESARIOS PARA REPORTES
            entrada: idkey,
            empresa: entrada.empresa,
            empresa_descripcion: entrada.empresa_descripcion,
            entrada_tipo: entrada.tipo,
            entrada_tipo_descripcion: entrada.tipo_descripcion,
            asunto: entrada.asunto,
            tema: entrada.tema,
          };

          if(item.id) {
            update(child(refFormasEnvio, item.id), oForma);
          } else {
            push(refFormasEnvio, oForma);
          }
        }

        resolve(idkey);
      } catch (error) {
        resolve(error);
      }

    });
  }

  guardarArchivos (idkey: any, archivos: any) {
    const refStorageEntrada = refStorage(this.storage, `/entradas/${idkey}`);
    return new Promise(async (resolve, reject) => {
      try {
        console.log('INICIA CARGA DE ARCHIVOS');
        let taskFiles: any[] = [];
        for(let i in archivos) {
          const archivo = archivos[i];
          if(archivo.file) {
            const refStorageEntradaFile = refStorage(refStorageEntrada, archivo.name);
            let uploadTask = uploadBytesResumable(refStorageEntradaFile, archivo.file);
            taskFiles.push(uploadTask);
            /*try {
              await uploadTask;
              console.log('El archivo se ha subido por completo!');
            } catch (error) {
              console.error('Error durante la subida:', error);
            }*/
          } 
        }
        
        this.taskFilesEntrada = taskFiles;
        return Promise.all(taskFiles).then(
          (values) => {
            console.log('TERMINA DE CARGA DE ARCHIVOS TERMINADA');
            resolve(idkey);
          },
          (reason) => {
            console.log('ERROR ENCARGA DE ARCHIVOS', reason);
            reject(reason);
          },
        );
          
        //resolve(idkey);
      } catch (error) {
        reject(error);
      }
    });
  }

  guardarAcuerdo (idkey: any, acuerdo: iAcuerdo) {
    return new Promise(async (resolve, reject) => {
      if(acuerdo.id_tipo_forma == 1 && acuerdo.id_tema != '') {
        //DETENEMOS PARA GENERAR EL REGISTRO EN SQL
        const duracion = await resolveAfterSeconds(1, "1 Segundo");
        console.log('guardarAcuerdo: ', duracion);

        //RELACIONAMOS LA ENTRADA
        const relacion: any = await firstValueFrom(this.http.post(environment.acuerdos.relacionarEntrada, {
          idEntrada: idkey,
          idTema: acuerdo.id_tema
        }));

        //ACTUALIZAMOS EL ACUERDO
        const actualizar: any =  await firstValueFrom(this.http.post(environment.acuerdos.guardarTema, {
          categorizacion: acuerdo.id_categorizacion,
          descripcionTema: acuerdo.descripcionTema.replace(/<[^>]*>/g, ''),
          descripcionTema_html: acuerdo.descripcionTema_html,
          esConfidencial: acuerdo.banEsConfidencial,
          id_acuerdo: acuerdo.id_forma_envio,
          id_categoria: acuerdo.id_categoria,
          id_tema: acuerdo.id_tema,
          indice: acuerdo.indice,
          nombreTema: acuerdo.nombreTema,
          respAnalista: acuerdo.id_fb_resp_analista,
          respSupervisor: acuerdo.id_fb_resp_supervisor,
          respCoordinador: acuerdo.id_fb_resp_coordinador,
          tipoTema: acuerdo.id_tipo_tema
        }));

        if(!relacion.error) {
          resolve(idkey);
        } else {
          reject(relacion);
        }

      } else if(acuerdo.id_tipo_forma == 2 && acuerdo.id_tema != '') {
        //DETENEMOS PARA GENERAR EL REGISTRO EN SQL
        const duracion = await resolveAfterSeconds(1, "1 Segundo");
        console.log('guardarAcuerdo: ', duracion);

        //RELACIONAMOS LA ENTRADA
        const relacion: any = await firstValueFrom(this.http.post(environment.despacho.relacionarEntrada, {
          id_tema: acuerdo.id_tema,
          id_entrada: idkey
        }));

        //ACTUALIZAMOS EL ACUERDO
        const actualizar: any =  await firstValueFrom(this.http.post(environment.despacho.guardarTema, {
          descripcionTema: acuerdo.descripcionTema,
          id_empresa: acuerdo.id_empresa,
          id_tema: acuerdo.id_tema,
          id_tipo_despacho: acuerdo.id_tipo_despacho,
          nombreTema: acuerdo.nombreTema,
          nombre_empresa: acuerdo.nombre_empresa
        }));

        if(!relacion.error) {
          resolve(idkey);
        } else {
          reject(relacion);
        }
        
      } else {
        resolve('SIN ACUERDO');
      }
    })
  }

  guardaSeguimiento (idkey: any, entrada: any) {
    const promiseSeguimiento = new Promise((resolve, reject) => {

      try {
        const aRemitentes = 
          (Array.isArray(entrada.remitente_enviado) && entrada.remitente_enviado.length > 0)? entrada.remitente_enviado:
            (Array.isArray(entrada.delegado) && entrada.delegado.length > 0) ? entrada.delegado: [];

        const refSeguimiento = ref(this.database, `seguimiento/${idkey}`);
        set(refSeguimiento, aRemitentes);

        if(entrada.idPadre) {
          const refRemitentes = ref(this.database, `remitentes/${entrada.idPadre}/${idkey}`);
          set(refRemitentes, {
            [entrada.remitente]: true
          });
        }

        return resolve(idkey);
      } catch (error) {
        return reject(error);
      }
    });

    return promiseSeguimiento;
  }

  guardarEntregables (idkey: string, entrada: iEntrada, entregables: iEntregables[], usuario: iUsuario) {
      this.taskFilesEntregables = [];
      const refEntregables = ref(this.database, `entregables/${idkey}`);
      const referenciaLogger = ref(this.database, `entregables_logger/${idkey}`);
  
      return new Promise(async (resolve, reject) => {
        let aProsesosArchivos: any[] = [];
        try {

          for(let i in entregables) {
            let oEntregable = entregables[i];
            oEntregable.archivos = oEntregable.archivos.map((item:any) => { return {"name":item.name, "metadata":item.metadata} });

            if(oEntregable.id) {
              update(child(refEntregables, oEntregable.id), {
                //Archivos
                archivos: oEntregable.archivos,

                //DOCUMENTO
                url: oEntregable.url,
                title: oEntregable.title,
                idGas: oEntregable.idGas,
                used: (oEntregable.url != null)?true:false,

                //DATOS NECESARIOS PARA REPORTES
                fechaInicio: moment(entrada.fechaInicio).format("YYYY-MM-DD"),
                fechaFin: moment(entrada.fechaFin).format("YYYY-MM-DD"),
                entrada: idkey,
                tema: entrada.tema,
                asunto: entrada.asunto,
                analista: entrada.analista,
                analista_descripcion: entrada.analista_descripcion,
                supervisor: entrada.supervisor,
                supervisor_descripcion: entrada.supervisor_descripcion,
                coordinador: entrada.coordinador,
                coordinador_descripcion: entrada.coordinador_descripcion,
                timestamp: new Date().getTime()
              });

              aProsesosArchivos = aProsesosArchivos.concat(this.guardarArchivosEntregable(oEntregable.id, oEntregable.files));
            } else {
              let idEntregable = push(refEntregables, {
                tipo: oEntregable.tipo,
                tipo_descripcion: oEntregable.tipo_descripcion,
                estatus: oEntregable.estatus,
                estatus_descripcion: oEntregable.estatus_descripcion,
                comentarios: oEntregable.comentarios,
                archivos: oEntregable.archivos,
                unameUpdate: usuario.nombre,
                umailUpdate: usuario.mail,
                prioridad: 2,

                //DOCUMENTO
                url: oEntregable.url,
                title: oEntregable.title,
                idGas: oEntregable.idGas,
                used: (oEntregable.url != null)?true:false,

                //DATOS NECESARIOS PARA REPORTES
                fechaInicio: moment(entrada.fechaInicio).format("YYYY-MM-DD"),
                fechaFin: moment(entrada.fechaFin).format("YYYY-MM-DD"),
                entrada: idkey,
                tema: entrada.tema,
                asunto: entrada.asunto,
                analista: entrada.analista,
                analista_descripcion: entrada.analista_descripcion,
                supervisor: entrada.supervisor,
                supervisor_descripcion: entrada.supervisor_descripcion,
                coordinador: entrada.coordinador,
                coordinador_descripcion: entrada.coordinador_descripcion,
                creacion: moment().format("YYYY-MM-DD HH:mm:ss"),
                terminado: moment().format("YYYY-MM-DD HH:mm:ss"),
                timestamp: new Date().getTime()
              }).key;

              aProsesosArchivos = aProsesosArchivos.concat(this.guardarArchivosEntregable(idEntregable, oEntregable.files));   

              //LOGGER PARA ENTREGABLES
              let idLogger = push(referenciaLogger, {
                id: idEntregable,
                tipo_descripcion: oEntregable.tipo_descripcion,
                estatus: oEntregable.estatus,
                estatus_descripcion: oEntregable.estatus_descripcion,
                analista_descripcion: entrada.analista_descripcion,
                supervisor_descripcion: entrada.supervisor_descripcion,
                coordinador_descripcion: entrada.coordinador_descripcion,
                motivo_cancelacion: oEntregable.motivo_cancelacion,
                timestamp: new Date().getTime(),
                //USUARIO QUE REALIZA ACTUALIZACION
                unameUpdate: usuario.nombre,
                umailUpdate: usuario.mail
              });
            }
          }

          return Promise.all(aProsesosArchivos).then(values => {
            console.log("TERMINA DE CARGA DE ENTREGABLES");
            resolve(idkey);
          }).catch(reason => {
            reject(reason);
          });
          
        } catch (error) {
          reject(error);
        }
      });
  }

  guardarRelacion (idkey: string, oEntrada: iEntrada, oPadre: iEntrada, oCatalogos: iCatalogos) {
    const promiseRelacion = new Promise((resolve, reject) => {
      let isfind: boolean = false;
      let catTipo: any = oCatalogos.catTipo;

      try {
        //PASAMOS REFERENCIA DEL HIJO
        let aRelacion = (oPadre.idRelacionado != null)? oPadre.idRelacionado : [];

        for(let i in aRelacion) {
          if(aRelacion[i].includes(idkey))
            isfind = true;
        }

        if(isfind == false) {
          aRelacion.push(`${oEntrada.tema}/${idkey}`);
        }

        oPadre.idRelacionado = aRelacion;

        //PASAMOS EL TIPO SI LA PRIORIDAD ES MENOR
        var prioridadPadre = (catTipo[oPadre.tipo] && catTipo[oPadre.tipo].prioridad)? parseInt(catTipo[oPadre.tipo].prioridad) : 100;
        var prioridadHijo = (catTipo[oEntrada.tipo] && catTipo[oEntrada.tipo].prioridad)? parseInt(catTipo[oEntrada.tipo].prioridad) : 100;
        if(prioridadPadre > prioridadHijo) {
            oPadre.tipo = oEntrada.tipo;
            oPadre.tipo_descripcion = oEntrada.tipo_descripcion;
        }

        //PASAMOS LA FEHCA FIN SI ES MENOR
        var fechaPadre = moment(oPadre.fechaFin).valueOf();
        var fechaHijo = moment(oEntrada.fechaFin).valueOf();
        if(fechaPadre < fechaHijo) {
            oPadre.fechaFin = oEntrada.fechaFin;
            oPadre.end = oEntrada.end;

            if(oPadre.estatus_descripcion.toUpperCase().includes("TERMINADO")) {
                oPadre.estatus = oEntrada.estatus;
                oPadre.estatus_descripcion = oEntrada.estatus_descripcion;
            }
        }

        const refEntradaPadre = ref(this.database, `entrada/${oEntrada.idPadreTema}/${oEntrada.idPadre}`);
        update(refEntradaPadre, oPadre).then(() => {
          return resolve(idkey);
        })
        .catch((error) => {
          return reject(error);
        });
      } catch(error) {
        reject({idTransaccion: idkey, error: error})
      }
    });

    return promiseRelacion;
  }

  obtenerCatalogoEstadoEntregales () {
    return {
      estados: [
        "Nuevo", //0
        "Asignado", //1
        "Enviado a Supervisor", //2
        "Enviado a Coordinador", //3
        "Enviado a Ejecución", //4
        "Terminado", //5
        "Cancelado", //6
      ],
      catalogo: [
        //Analista
        { id: 2, estatus: 1, descripcion: "Enviar a supervisor" },
        { id: 9, estatus: 1, descripcion: "Guardar entregable" },
        { id: 6, estatus: 1, descripcion: "Cancelar entregable" },

        //Supervisor
        { id: 1, estatus: 2, descripcion: "Regresar analista" },
        { id: 3, estatus: 2, descripcion: "Enviar a coordinador" },
        { id: 9, estatus: 2, descripcion: "Guardar entregable" },
        { id: 6, estatus: 2, descripcion: "Cancelar entregable" },

        //Coordinador
        { id: 2, estatus: 3, descripcion: "Regresar supervisor" },
        { id: 4, estatus: 3, descripcion: "Enviar a ejecución" },
        { id: 9, estatus: 3, descripcion: "Guardar entregable" },
        { id: 6, estatus: 3, descripcion: "Cancelar entregable" },

        //Ejecucion
        { id: 2, estatus: 4, descripcion: "Regresar supervisor" },
        { id: 5, estatus: 4, descripcion: "Enviar a terminado" },
        { id: 9, estatus: 4, descripcion: "Guardar entregable" },
        { id: 6, estatus: 4, descripcion: "Cancelar entregable" },
      ],
    }
  }

  bloqueoEntrada (idkey: string, idTema: string, usuario: iUsuario) {
    const referenciaBloqueo = ref(this.database, `/entrada_lock/${idkey}/lock`);
    update(referenciaBloqueo, {
      id: usuario.uid,
      nombre:usuario.nombre,
      timestamp: new Date().getTime() 
    });
  }

  obtenerEntradaLock (idkey:string) {
    const referenciaEntradaLock = ref(this.database, `/entrada_lock/${idkey}/lock`);
    return objectVal(referenciaEntradaLock);
  }

  obtenerReferenciaLock (idkey:string) {
    const referenciaEntradaLock = ref(this.database, `/entrada_lock/${idkey}`);
    return referenciaEntradaLock;
  }

  obtenerBitacora (oData: any) {
    return this.http.get(environment.entregables.bitacora, {
      params: oData,
      headers: { "Content-Type": "application/json" },
      responseType: "arraybuffer",
    }).pipe(
      tap((data) => console.log("API Response", data)),
      take(1),
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        return of({
          error: true,
          message: "No se encontrarón datos para esta consulta.",
        } as unknown as any);
      })
    );
  }

  enviaNotificacion (oNotificiaciones: any) {
    return firstValueFrom(this.http.post(environment.notificaciones.create, oNotificiaciones));
  }

  borrarEntrada (idTema: string, idEntrada: string) {
    const refEntrada = ref(this.database, `/entrada/${idTema}/${idEntrada}`);
    return remove(refEntrada);
  }

  async actualizamosTemaHijos (idTema: string, idEntrada: string) {
    const temas = await Promise.all([
      get(query(ref(this.database, `/entrada/-KcOsYIoTX3wdcK4DMjF`), orderByChild('idPadre'), equalTo(idEntrada))),
      get(query(ref(this.database, `/entrada/-KcOsYIkmr2FHgi4ubym`), orderByChild('idPadre'), equalTo(idEntrada))),
      get(query(ref(this.database, `/entrada/-KcOsYIprL1TVCF2uyaO`), orderByChild('idPadre'), equalTo(idEntrada))),
      get(query(ref(this.database, `/entrada/-KdfegAIz9pHjb6ak32K`), orderByChild('idPadre'), equalTo(idEntrada)))
    ]);

    for(let i in temas) {
      let tema = temas[i].val();
      if(tema != null) {
        for(var key in tema) {
          if(key && tema[key] && tema[key].tema) {
            console.log("entrada/" + tema[key].tema + "/" + key);
            const refEntrada = ref(this.database, `entrada/${tema[key].tema}/${key}`);
            return update(refEntrada, {idPadreTema: idTema});
          }
        }
      } else {
        console.log("tema vacio");
      }
    }
  }

}